import React, { FC, useState, useCallback, useEffect } from "react";
import CommonForm from "../molecules/CommonForm";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Multiselect from "multiselect-react-dropdown";
import EditIcon from "../../static/images/Editicon.svg";
import DeleteIcon from "../../static/images/Archive.svg";
import "../../static/css/CrewDetails.css";
import Spinner from "../atoms/Spinner";
import { service } from "../../../services/AxiosServices";
import SectionHeader from "./SectionHeader";
import WindowHeightComponent from "../../Layouts/WindowHeight";
import x from '../../static/images/x.png'
import { format, parseISO } from 'date-fns'; 
import ConfirmDialog from "../atoms/ConfirmDialog";
import AccessControl from "./AccessControl";
import AccessDenied from "../atoms/AccessDenied";
import useAccessCheckLoading from "../../../utils/useAccessCheckLoading";

const SpaceForm: FC = () => {
  const { id } = useParams<{ id: string }>();
  const spaceId = Number(id);
  const navigate = useNavigate();

  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showDelete, setShowDelete] = useState(false);
  const accessCheckLoading = useAccessCheckLoading()
  const [formState, setFormState] = useState({
    values: {
      spaceName: "",
      description: "",
      associated_users: [] as number[],
      createdAt:"",
      updatedAt:""
    },
    clientErrors: {} as Record<string, string>,
    serverError: null as string | null,
  });
  const [initialValues, setInitialValues] = useState(formState.values);

  const [users, setUsers] = useState<{ id: number; name: string }[]>([]);
  // Function to get user names from selected user IDs
  const getUserNames = (userIds: number[]) => {
    return userIds
      .map((id) => {
        const user = users.find((u) => u.id === id);
        return user ? user.name : "";
      })
      .filter((name) => name !== "")
      .join(", ");
  };
  // Fetching users and space details when component mount
  useEffect(() => {
    if (spaceId) {
      // If space ID is available, fetch space details.
      const fetchSpace = async () => {
        setLoading(true);
        try {
          const usersResponse = await service<{ response: any }>(
            "/user-list",
            "post"
          );

          const usersData = usersResponse.data.response
            .map((user: any) => ({
              id: user.id,
              name:
                `${(user.first_name || "").trim()} ${(
                  user.last_name || ""
                ).trim()}`.trim() || user.email,
            }))
            .sort((a: any, b: any) => a.name.localeCompare(b.name));

          setUsers(usersData);

          const spaceResponse = await service<{ response: any }>(
            "api/get-spaces",
            "post"
          );
          const spaces = spaceResponse.data?.response || [];

          const space = spaces.find((s: any) => s.id === spaceId); // Finding the matching space.
    

          if (space) {
            const userIds = Array.isArray(space.associated_users)
              ? space.associated_users.map((user: any) =>
                  Number(user.id || user)
                )
              : space.associated_users
                  .split(",")
                  .map((id: string) => Number(id));
            const spaceData = {
              spaceName: space.space_name || "",
              description: space.space_description || "",
              associated_users: userIds,
              createdAt: space.uploaded_on ? format(parseISO(space.uploaded_on),'dd-MM-yyyy, HH:mm') :'',
              updatedAt: space.uploaded_on ? format(parseISO(space.recent_edited),'dd-MM-yyyy, HH:mm') :'',

            };
  
            setFormState((prev) => ({
              ...prev,
              values: spaceData,
            }));
            setInitialValues(spaceData);
          }
        } catch (err) {
          console.error("Error fetching data:", err);
        } finally {
          setLoading(false);
        }
      };

      if (spaceId) fetchSpace();
    }
    // Fetching user list separately.
    async function fetchData() {
      try {
        const response = await service<{ response: any }>("/user-list", "post");
        const usersData = response.data.response
          .map((user: any) => ({
            id: user.id,
            name:
              `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
              user.email,
          }))
          .sort((a: any, b: any) => a.name.localeCompare(b.name));

        setUsers(usersData);
      } catch (err) {
        console.error(err);
      }
    }
    fetchData();
  }, []);
  // Form fields configuration
  const fields = [
    {
      name: "spaceName",
      type: "text",
      label: "Knowledge hub name",
      required: true,
    },
    {
      name: "description",
      type: "textarea",
      label: "Description",
      rows: 5,
      cols: 50,
    },
  ];
  // Function to validate form inputs.
  const validations = (values: Record<string, any>) => {
    const errors: Record<string, string> = {};
    if (!values.spaceName.trim())
      errors.spaceName = "Space name  field is required";
    // if (!values.description.trim())
    //   errors.description = "Description field  is required";
    // if (!values.associated_users.length)
    //   errors.associated_users = "At least one user must be selected";
    return errors;
  };

  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) => {
      const { name, value } = e.target;
      setFormState((prev) => ({
        ...prev,
        values: { ...prev.values, [name]: value },
        ClientErrors: { ...prev.clientErrors, [name]: "" },
        serverError: null,
      }));
    },
    []
  );
  // Function to handle user selection from dropdown.
  const handleUserSelect = useCallback((selectedList: any[]) => {
    const selectedIds = selectedList.map((user) => user.id);
    setFormState((prev) => ({
      ...prev,
      values: { ...prev.values, associated_users: selectedIds },
      errors: { ...prev.clientErrors, associated_users: "" },
    }));
  }, []);
  // Function to remove users from selection.
  const handleUserRemove = useCallback((removedList: any[]) => {
    const remainingIds = removedList.map((user) => user.id);
    setFormState((prev) => ({
      ...prev,
      values: { ...prev.values, associated_users: remainingIds },
      errors: { ...prev.clientErrors, associated_users: "" },
    }));
  }, []);
  // Function to enable edit mode.
  const handleEdit = () => {
    setFormState((prev) => ({ ...prev, values: { ...prev.values } }));
    setIsEdit(true);
  };
  // Function to cancel editing and revert changes.
  const handleCancel = () => {
    setFormState((prev) => ({ ...prev, values: initialValues }));
    setIsEdit(false);
  };
  // Function to handle form submission.
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const errors = validations(formState.values);
      if (Object.keys(errors).length === 0) {
        try {
          const requestBody = {
            project_id: localStorage.getItem("project_id"),
            ...(spaceId && { space_id: spaceId }),
            space_name: formState.values.spaceName,
            description: formState.values.description,
            associated_users: formState.values.associated_users.join(","),
            uploaded_by: localStorage.getItem("user"),
          };

          console.log(requestBody)
          const response = await service(
            "api/create-space",
            "post",
            requestBody
          );

          toast.success(
            `Knowledge hub ${spaceId ? "updated" : "created"} successfully!`,
            { autoClose: 2000 }
          );
          {
            spaceId && setIsEdit(false);
          }
          {
            spaceId
              ? navigate(`/space/${spaceId}`, {
                  state: { spaceName: formState.values.spaceName },
                })
              : navigate("/knowledge-hubs");
          }
        } catch (err: any) {
          const errorMessage =
            err.response?.data?.error ||
            "Something went wrong. Please try again..";
          console.error(errorMessage);
          setFormState((prev) => ({ ...prev, serverError: errorMessage }));
        }
      } else {
        setFormState((prev) => ({ ...prev, clientErrors: errors }));
      }
    },
    [formState.values, navigate]
  );
  const clientErrorsArray = Object.entries(formState.clientErrors).reduce(
    (acc: string[], [field, error]) => {
      if (error) {
        acc.push(`${error}`);
      }
      return acc;
    },
    []
  );
  const handleDelete = async () => {
    setShowDelete(true);
  };
  
  const confirmDelete = async () => {
    try{
      await service(`/api/delete-space`, "post", {
        space_id: id,
      });
      toast.success("Knowledge hub deleted successfully", { autoClose: 2000 });
       navigate('/knowledge-hubs')
    }
    catch(err){
      console.log(err)
    }
    finally{
      setShowDelete(false)
    }
  };

  return (
    <>

      <AccessControl
        requiredPermissions={[
          {
            permission: "manage_spaces",
            types: spaceId ? ["read"] : ["create"],
          },
        ]}
        renderNoAccess={accessCheckLoading ? <Spinner/> : <AccessDenied />}
      >
        {/* Create mode - no spaceId */}
        {!spaceId && (
          <>
            <SectionHeader back={true} title={"Create knowledge hub"} />
            
            {formState.serverError && (
              <div className="alert alert-danger">{formState.serverError}</div>
            )}
            {clientErrorsArray.length > 0 && (
              <div className="alert alert-danger">
                <ul>
                  {clientErrorsArray.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <WindowHeightComponent className="">
              <div className="p-3">
                <CommonForm
                  fields={fields}
                  values={formState.values}
                  onChange={handleInputChange}
                  onSubmit={handleSubmit}
                >
                  <label htmlFor="associated_users" className="LabelText mb-1">
                    Select users:
                  </label>
                  <div className="custom-multiselect">
                    <Multiselect
                      options={users}
                      selectedValues={users.filter((user) =>
                        formState.values.associated_users.includes(user.id)
                      )}
                      onSelect={handleUserSelect}
                      onRemove={handleUserRemove}
                      displayValue="name"
                      id="associated_users"
                      customCloseIcon={<img style={{ width: "1rem", height: "1rem", marginLeft:'1rem' }} src={x} alt="close" />}
                    />
                  </div>
                </CommonForm>
              </div>
            </WindowHeightComponent>
          </>
        )}

        {spaceId && (
          <>
            <div className="container-fluid">
              <div className="row">
                <div className="col-md-12 p-0">
                  <SectionHeader
                    back={true}
                    title={
                      isEdit ? "Edit knowledge hub" : "Knowledge hub details"
                    }
                  />
                </div>
              </div>
            </div>

            <WindowHeightComponent className="">
              <div className="container-fluid">
                <div className="row">
                  <div className="col-md-12 p-1">
                    <div className="space-details p-3">
                      {loading ? (
                        <Spinner />
                      ) : isEdit ? (
                        /* Edit mode */
                        <AccessControl
                          requiredPermissions={[
                            {
                              permission: "manage_spaces",
                              types: ["update"],
                            },
                          ]}
                          renderNoAccess={<AccessDenied />}
                        >
                          <>
                          <div className="">
                            <label
                              htmlFor="associated_users"
                              className="LabelText mb-1"
                            >
                              Select users
                            </label>
                            </div>
                            {formState.serverError && (
                              <div className="alert alert-danger">
                                {formState.serverError}
                              </div>
                            )}
                            {clientErrorsArray.length > 0 && (
                              <div className="alert alert-danger">
                                <ul>
                                  {clientErrorsArray.map((error, index) => (
                                    <li key={index}>{error}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            <CommonForm
                              fields={fields}
                              values={formState.values}
                              onChange={handleInputChange}
                              onSubmit={handleSubmit}
                              onCancel={handleCancel}
                              isEdit={isEdit}
                            >
                              <div className="ms-2">
                                <label
                                  htmlFor="associated_users"
                                  className="LabelText mb-1"
                                >
                                  Select users
                                </label>
                                <Multiselect
                                  options={users}
                                  selectedValues={users.filter((user) =>
                                    formState.values.associated_users.includes(
                                      user.id
                                    )
                                  )}
                                  onSelect={handleUserSelect}
                                  onRemove={handleUserRemove}
                                  displayValue="name"
                                  id="associated_users"
                                  customCloseIcon={<img style={{ width: "1rem", height: "1rem", marginLeft:'1rem' }} src={x} alt="close" />}
                                />
                              </div>
                            </CommonForm>
                          </>
                        </AccessControl>
                      ) : (
                        /* Details/View mode */
                        <>
                          <div className="d-flex postition-relative">
                            {/* Delete button with access control */}
                            <AccessControl
                              requiredPermissions={[
                                {
                                  permission: "manage_spaces",
                                  types: ["delete"],
                                },
                              ]}
                              renderNoAccess={<></>}
                            >
                              <button
                                onClick={handleDelete}
                                className="ms-2 space-delete-btn"
                                title="Delete"
                              >
                                <img
                                  src={DeleteIcon}
                                  alt="Delete"
                                  className="me-2"
                                />
                              </button>
                            </AccessControl>

                            {/* Edit button with access control */}
                            <AccessControl
                              requiredPermissions={[
                                {
                                  permission: "manage_spaces",
                                  types: ["update"],
                                },
                              ]}
                              renderNoAccess={<></>}
                            >
                              <button
                                onClick={handleEdit}
                                type="button"
                                className="me-1 space-detials-btn"
                              >
                                <img src={EditIcon} alt="Edit" className="icon" />
                              </button>
                            </AccessControl>
                          </div>
                          
                          {/* Details view with read access control */}
                          <AccessControl
                            requiredPermissions={[
                              {
                                permission: "manage_spaces",
                                types: ["read"],
                              },
                            ]}
                            renderNoAccess={<AccessDenied />}
                          >
                            <div className="details-container">
                              <div className="detail-item">
                                <span className="field-label">
                                  Knowledge hub name:
                                </span>
                                <span className="field-value">
                                  {formState.values.spaceName}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="field-label">Description:</span>
                                <span className="field-value">
                                  {formState.values.description}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="field-label">
                                  Associated users:
                                </span>
                                <span className="field-value">
                                  {getUserNames(formState.values.associated_users)}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="field-label">Created on:</span>
                                <span className="field-value">
                                  {formState.values.createdAt}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="field-label">Updated on:</span>
                                <span className="field-value">
                                  {formState.values.updatedAt}
                                </span>
                              </div>
                            </div>
                          </AccessControl>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <ConfirmDialog
                show={showDelete}
                onHide={() => setShowDelete(false)}
                onConfirm={confirmDelete}
                title="Confirm delete"
                body="Are you sure?"
                confirmText="Delete"
              />
            </WindowHeightComponent>
          </>
        )}
      </AccessControl>
    </>
  );
};

export default SpaceForm;